import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material';
import React, {useContext, useState} from 'react';
import dayjs from 'dayjs';
import ZeiterfassungSelectBauvorhaben from './ZeiterfassungSelectBauvorhaben';
import ZeiterfassungSelectArbeitszeiten from './ZeiterfassungSelectArbeitszeiten';
import ZeiterfassungContext from '../../../../Contexts/ZeiterfassungContext/ZeiterfassungContext';
import ZeiterfassungStatus from './ZeiterfassungStatus';
import ZeiterfassungTaetigkeit from './ZeiterfassungTaetigkeit';
import {Cancel, Save, Warning} from '@mui/icons-material';

export default function ZeiterfassungNeueZeitmeldung() {
    const {
        createNeueZeitmeldung,
        setCreateNeueZeitmeldung,
        resetForm,
        sendArbeitszeit,
        bauvorhaben,
        startZeit,
        endeZeit,
        pauseZeit,
        taetigkeit,
        editMode,
        setEditMode,
        updateArbeitszeit,
        setCurrentEditId,
        isUrlaub,
        isKrank,
        isSchlechtwetter,
        arbeitszeitenDay,
        currentEditId,
    } = useContext(ZeiterfassungContext);

    const [validationError, setValidationError] = useState<string | null>(null);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);
    const [duplicateWarningOpen, setDuplicateWarningOpen] = useState<boolean>(false);

    // Check for duplicate entries with same start and end time on the same date
    const checkForDuplicate = () => {
        if (!startZeit || !endeZeit || !arbeitszeitenDay) return false;

        // Convert times to strings for comparison
        const newStartTime = startZeit.format('HH:mm:ss');
        const newEndTime = endeZeit.format('HH:mm:ss');

        // Check if there's an existing entry with the same start and end times
        // Exclude the current entry being edited
        const duplicateEntry = arbeitszeitenDay.find((entry) => {
            // Skip the current entry being edited
            if (editMode && entry.id === currentEditId) return false;

            const existingStartTime = dayjs(entry.start).format('HH:mm:ss');
            const existingEndTime = dayjs(entry.ende).format('HH:mm:ss');

            return existingStartTime === newStartTime && existingEndTime === newEndTime;
        });

        return duplicateEntry !== undefined;
    };

    // Validation function - only returns validation result, doesn't set error message
    const validateForm = () => {
        // Check if bauvorhaben is selected
        if (!bauvorhaben || bauvorhaben.length === 0 || bauvorhaben === 'null') {
            return {
                isValid: false,
                errorMessage: 'Bitte wählen Sie ein Bauvorhaben aus',
            };
        }

        // Check if times are valid
        if (!startZeit || !endeZeit || !pauseZeit) {
            return {
                isValid: false,
                errorMessage: 'Start- und Endzeit müssen angegeben werden',
            };
        }

        if (bauvorhaben.toLowerCase().includes('sonstiges') && !taetigkeit) {
            return {
                isValid: false,
                errorMessage: 'Bitte füllen Sie die Tätigkeit / Info aus',
            };
        }

        // Ensure start time is before end time
        if (!startZeit.isBefore(endeZeit)) {
            return {
                isValid: false,
                errorMessage: 'Die Startzeit muss vor der Endzeit liegen',
            };
        }

        // Ensure pause is not longer than total work time
        if (pauseZeit.diff(dayjs('1900-01-01T00:00')) >= endeZeit.diff(startZeit)) {
            return {
                isValid: false,
                errorMessage: 'Die Pausenzeit darf nicht länger als die Arbeitszeit sein',
            };
        }

        return {
            isValid: true,
            errorMessage: null,
        };
    };

    const handleClick_Abbrechen = () => {
        resetForm();
        setCreateNeueZeitmeldung(false);
        setHasAttemptedSubmit(false);
        if (editMode) {
            setEditMode(false);
            setCurrentEditId(-1);
        }
    };

    const handleClick_Save = () => {
        // Set flag that user has attempted to submit
        setHasAttemptedSubmit(true);

        // Validate the form
        const validation = validateForm();
        setValidationError(validation.errorMessage);

        // If not valid, don't proceed
        if (!validation.isValid) {
            return;
        }

        // Check for duplicates (only for new entries, not when editing)
        if (!editMode && checkForDuplicate()) {
            setDuplicateWarningOpen(true);
            return;
        }

        // Form is valid and no duplicates, proceed with save/update
        proceedWithSave();
    };

    const proceedWithSave = () => {
        // Form is valid, proceed with save/update
        if (editMode) {
            updateArbeitszeit();
        } else {
            sendArbeitszeit();
        }

        // Reset states
        resetForm();
        setCreateNeueZeitmeldung(false);
        setHasAttemptedSubmit(false);
        if (editMode) {
            setEditMode(false);
            setCurrentEditId(-1);
        }
    };

    const handleDuplicateCancel = () => {
        setDuplicateWarningOpen(false);
    };

    const handleDuplicateProceed = () => {
        setDuplicateWarningOpen(false);
        proceedWithSave();
    };

    // Determine entry type for styling
    const getEntryTypeColor = () => {
        if (isUrlaub) return '#1976d2';
        if (isKrank) return '#d32f2f';
        if (isSchlechtwetter) return '#ed6c02';
        return '#2e7d32';
    };

    const getEntryType = () => {
        if (isUrlaub) return 'Urlaub';
        if (isKrank) return 'Krank';
        if (isSchlechtwetter) return 'Schlechtwetter';
        return 'Arbeitszeit';
    };

    if (!createNeueZeitmeldung) {
        return null;
    }

    // Get current validation result but only show error if user has attempted to submit
    const currentValidation = validateForm();
    const showValidationError = hasAttemptedSubmit && !currentValidation.isValid;

    return (
        <Box sx={{width: '100%', overflow: 'hidden'}}>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                {/* Header with title */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        pb: 2,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    }}>
                    <Box>
                        <Typography variant='h6' sx={{fontWeight: 'bold'}}>
                            {editMode ? 'Zeiteintrag bearbeiten' : 'Neue Zeitmeldung erstellen'}
                        </Typography>
                        <Box
                            sx={{
                                mt: 0.5,
                                bgcolor: getEntryTypeColor() + '15',
                                color: getEntryTypeColor(),
                                fontWeight: 'medium',
                                px: 1,
                                py: 0.3,
                                display: 'inline-block',
                            }}>
                            {getEntryType()}
                        </Box>
                    </Box>
                </Box>

                {/* Error message display - only show after submission attempt */}
                {showValidationError && (
                    <Alert
                        severity='error'
                        sx={{
                            mb: 2,
                        }}>
                        {currentValidation.errorMessage}
                    </Alert>
                )}

                {/* Form components */}
                <Box sx={{mb: 3}}>
                    <Typography variant='subtitle2' sx={{mb: 1, color: 'text.secondary'}}>
                        Status
                    </Typography>
                    <ZeiterfassungStatus/>
                </Box>

                <Box sx={{mb: 3}}>
                    <Typography
                        variant='subtitle2'
                        sx={{
                            mb: 1,
                            color: hasAttemptedSubmit && !bauvorhaben ? 'error.main' : 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                        }}>
                        Bauvorhaben
                        <Typography component='span' variant='caption' color='error' sx={{ml: 1}}>
                            (Erforderlich)
                        </Typography>
                    </Typography>
                    <ZeiterfassungSelectBauvorhaben/>
                    {/* {!editMode && <ZeiterfassungNeuesBauvorhaben />} */}
                </Box>

                <Box sx={{mb: 3}}>
                    <Typography
                        variant='subtitle2'
                        sx={{
                            mb: 1,
                            color:
                                hasAttemptedSubmit && (!startZeit || !endeZeit) ? 'error.main' : 'text.secondary',
                        }}>
                        Zeiten
                    </Typography>
                    <ZeiterfassungSelectArbeitszeiten/>
                </Box>

                <Box sx={{mb: 3}}>
                    <Typography variant='subtitle2' sx={{mb: 1, color: 'text.secondary'}}>
                        Tätigkeit
                    </Typography>
                    <ZeiterfassungTaetigkeit/>
                </Box>

                {/* Action buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        pt: 3,
                        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                    }}>
                    <Button
                        variant='outlined'
                        color='primary'
                        onClick={handleClick_Abbrechen}
                        fullWidth
                        sx={{
                            py: 1.2,
                        }}
                        startIcon={<Cancel/>}>
                        Abbrechen
                    </Button>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={handleClick_Save}
                        fullWidth
                        sx={{
                            py: 1.2,
                            fontWeight: 'medium',
                            boxShadow: 2,
                        }}
                        startIcon={<Save/>}>
                        {editMode ? 'Speichern' : 'Hinzufügen'}
                    </Button>
                </Box>
            </Box>

            {/* Duplicate Warning Dialog */}
            <Dialog
                open={duplicateWarningOpen}
                onClose={handleDuplicateCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <DialogTitle sx={{pb: 2}}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                backgroundColor: 'warning.50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Warning sx={{color: 'warning.main', fontSize: 20}}/>
                        </Box>
                        <Typography variant="h6" fontWeight={600}>
                            Doppelter Eintrag erkannt
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{pb: 2}}>
                    <Typography variant="body1" sx={{mb: 2}} color="text.secondary">
                        Es existiert bereits ein Zeiteintrag für das ausgewählte Datum mit derselben Start- und Endzeit
                        ({startZeit?.format('HH:mm')} - {endeZeit?.format('HH:mm')}).
                    </Typography>
                    <Alert severity="warning" sx={{borderRadius: 2}} id={'duplicate-warning'}>
                        <Typography variant="body2" fontWeight={500}>
                            Möchten Sie trotzdem fortfahren und einen doppelten Eintrag erstellen?
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 3, pt: 1}}>
                    <Button
                        onClick={handleDuplicateCancel}
                        color="inherit"
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 3,
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        id={'duplicate-proceed'}
                        onClick={handleDuplicateProceed}
                        variant="contained"
                        color="warning"
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.2)',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        Trotzdem hinzufügen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
